import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEP_UP_TTL_MS = 30 * 60 * 1000; // 30 minutes

const key = (userId, tab) => `valueplus.stepup.${userId}.${tab}`;

const getLastVerifiedAt = (userId, tab) => {
  try { return parseInt(localStorage.getItem(key(userId, tab)) || '0', 10) || 0; }
  catch { return 0; }
};
const markVerifiedNow = (userId, tab) => {
  try { localStorage.setItem(key(userId, tab), String(Date.now())); }
  catch { /* noop */ }
};

/**
 * useSensitive2FAGate
 *
 * For users with push 2FA enabled, requires a fresh push-approval every
 * STEP_UP_TTL_MS before granting access to a sensitive tab (Dokumente or
 * Postfach). Clients call requestAccess() before rendering tab content.
 *
 * For users without 2FA active, this hook is a no-op.
 */
export default function useSensitive2FAGate(user, tabName) {
  const [challenge, setChallenge] = useState(null); // { token, number, ttl_seconds }
  const [unlocked, setUnlocked] = useState(false);
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  // Initial check on mount / when user/tab changes
  useEffect(() => {
    if (!user) return;
    if (!user.twofa_push_enabled) {
      setUnlocked(true);
      return;
    }
    const last = getLastVerifiedAt(user.id, tabName);
    if (Date.now() - last < STEP_UP_TTL_MS) {
      setUnlocked(true);
    } else {
      setUnlocked(false);
    }
  }, [user, tabName]);

  const requestAccess = useCallback(async () => {
    if (!user?.twofa_push_enabled) { setUnlocked(true); return; }
    setError('');
    try {
      const res = await axios.post(`${API}/2fa/push/challenge`, { purpose: 'STEP_UP' });
      setChallenge({
        token: res.data.challenge_token,
        number: res.data.number_code,
        ttl_seconds: res.data.ttl_seconds || 90,
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Konnte 2FA-Anfrage nicht starten');
    }
  }, [user]);

  // Polling for approval
  useEffect(() => {
    if (!challenge) return;
    let stopped = false;
    const tick = async () => {
      try {
        const r = await fetch(`${API}/2fa/push/challenge/${challenge.token}`);
        const data = await r.json();
        if (stopped) return;
        if (data.status === 'APPROVED') {
          markVerifiedNow(user.id, tabName);
          setUnlocked(true);
          setChallenge(null);
        } else if (data.status === 'DENIED') {
          setError('Anfrage wurde abgelehnt');
          setChallenge(null);
        } else if (data.status === 'EXPIRED') {
          setError('Anfrage abgelaufen');
          setChallenge(null);
        }
      } catch { /* noop */ }
    };
    tick();
    pollRef.current = setInterval(tick, 2000);
    return () => { stopped = true; clearInterval(pollRef.current); };
  }, [challenge, user, tabName]);

  const cancel = () => { setChallenge(null); setError(''); };

  return {
    unlocked,
    challenge,
    error,
    requestAccess,
    cancel,
    needsAuth: !unlocked && !!user?.twofa_push_enabled,
  };
}
